import { ensureRouteData, prefetchRouteData } from '@/app/query-client'
import { indexSummaryQueryOptions } from '@/features/market/api/queries'
import {
  INDEX_PANEL_DEFAULT_RANGE,
  marketOverviewQueryOptions,
} from '@/features/market-overview/api/queries'
import type { RouterContext } from '@/routes/layouts/root'

/**
 * The `/market` loader.
 *
 * MAIN: the overview, awaited — nothing on the tab can be drawn without it.
 *
 * SECONDARY, started and not awaited: the index panel's session chart, which
 * needs nothing from the overview, so it goes out alongside it rather than
 * one round trip after the page mounts.
 *
 * Its own module, as `app.guard.ts` is, so a test can mount it under a root
 * of its own: typed inside `createFileRoute`, a loader is bound to this
 * route's parent in the generated tree. It imports from `api/` modules only —
 * route loaders are not code-split, so anything imported here ships in the
 * entry chunk.
 *
 * SECONDARY reads only run on a real navigation (`!preload`): the main data
 * still loads on hover (`ensureRouteData` below is unconditional), but a
 * member's cursor crossing five links to `/market` must not fire five
 * sessions' worth of index-panel requests for pages they never open.
 */
export async function loadMarketOverview({
  context: { queryClient },
  preload,
}: {
  context: RouterContext
  preload: boolean
}): Promise<void> {
  if (!preload) {
    prefetchRouteData(queryClient, indexSummaryQueryOptions(INDEX_PANEL_DEFAULT_RANGE))
  }
  await ensureRouteData(queryClient, marketOverviewQueryOptions())
}
